import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Search, SlidersHorizontal, Droplets, Zap, Wifi, Check, ChevronDown, Home, Sparkles, Building, Calendar } from 'lucide-react';
import UtilityCheckboxes from '@/components/UtilityCheckboxes';
import {
  CONDITION_STATUS_OPTIONS, CONDITION_STATE_OPTIONS, FLOOR_LEVEL_OPTIONS,
  FEATURE_OPTIONS, BEDROOM_OPTIONS, BATHROOM_OPTIONS, PUBLICATION_WINDOW_OPTIONS,
} from '@/lib/propertyTaxonomy';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchBar({ compact = false, initialValues = {}, autoApply = false }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stateKey, setStateKey] = useState(initialValues.state || '');
  const [city, setCity] = useState(initialValues.city || '');
  const [cityMode, setCityMode] = useState('preset'); // 'preset' | 'other'
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(initialValues.property_type ? initialValues.property_type.split(',').filter(Boolean) : []);
  const [operationType, setOperationType] = useState(initialValues.operation_type || '');
  const [minPrice, setMinPrice] = useState(initialValues.min_price || '');
  const [maxPrice, setMaxPrice] = useState(initialValues.max_price || '');
  const [waterSupply, setWaterSupply] = useState(initialValues.water_supply ? initialValues.water_supply.split(',') : []);
  const [electricity, setElectricity] = useState(initialValues.electricity ? initialValues.electricity.split(',') : []);
  const [connectivity, setConnectivity] = useState(initialValues.connectivity ? initialValues.connectivity.split(',') : []);
  const [bedrooms, setBedrooms] = useState(initialValues.bedrooms ? initialValues.bedrooms.split(',').filter(Boolean) : []);
  const [bathrooms, setBathrooms] = useState(initialValues.bathrooms ? initialValues.bathrooms.split(',').filter(Boolean) : []);
  const [conditionStatus, setConditionStatus] = useState(initialValues.condition_status ? initialValues.condition_status.split(',').filter(Boolean) : []);
  const [conditionState, setConditionState] = useState(initialValues.condition_state ? initialValues.condition_state.split(',').filter(Boolean) : []);
  const [floorLevel, setFloorLevel] = useState(initialValues.floor_level ? initialValues.floor_level.split(',').filter(Boolean) : []);
  const [features, setFeatures] = useState(initialValues.features ? initialValues.features.split(',').filter(Boolean) : []);
  const [postedWithin, setPostedWithin] = useState(initialValues.posted_within || 'any');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAttributes, setShowAttributes] = useState(false);

  const PROPERTY_TYPES = [
    { value: 'house', label: t('types.house') },
    { value: 'apartment', label: t('types.apartment') },
    { value: 'land', label: t('types.land') },
    { value: 'warehouse', label: t('types.warehouse') },
    { value: 'office', label: t('types.office') },
    { value: 'commercial_unit', label: t('types.commercial_unit') },
  ];

  useEffect(() => {
    axios.get(`${API}/locations/states`).then(r => setStates(r.data.states || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!stateKey) { setCities([]); return; }
    axios.get(`${API}/locations/cities`, { params: { state: stateKey } })
      .then(r => {
        const list = r.data.cities || [];
        setCities(list);
        if (city && !list.some(c => c.name === city)) setCityMode('other');
      })
      .catch(() => setCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  const onStateChange = (val) => {
    const v = val === 'all' ? '' : val;
    setStateKey(v);
    setCity('');
    setCityMode('preset');
    if (autoApply) submitWith({ state: v, city: '' });
  };

  const onCitySelect = (val) => {
    if (val === '__other__') {
      setCityMode('other');
      setCity('');
      return;
    }
    setCityMode('preset');
    const newCity = val === 'all' ? '' : val;
    setCity(newCity);
    if (autoApply) submitWith({ city: newCity });
  };

  const toggleType = (val) => {
    setSelectedTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const resetAll = () => {
    setStateKey(''); setCity(''); setCityMode('preset'); setSelectedTypes([]); setOperationType('');
    setMinPrice(''); setMaxPrice(''); setWaterSupply([]); setElectricity([]); setConnectivity([]);
    setBedrooms([]); setBathrooms([]); setConditionStatus([]); setConditionState([]);
    setFloorLevel([]); setFeatures([]); setPostedWithin('any');
  };

  const submitWith = (overrides = {}) => {
    // Compose URL params from current local state, allowing per-call overrides.
    const v = {
      state: stateKey, city, selectedTypes, operationType, minPrice, maxPrice,
      waterSupply, electricity, connectivity, bedrooms, bathrooms,
      conditionStatus, conditionState, floorLevel, features, postedWithin,
      ...overrides,
    };
    const params = new URLSearchParams();
    if (v.state) params.set('state', v.state);
    if (v.city) params.set('city', v.city);
    if (v.selectedTypes?.length) params.set('property_type', v.selectedTypes.join(','));
    if (v.operationType && v.operationType !== 'all') params.set('operation_type', v.operationType);
    if (v.minPrice) params.set('min_price', v.minPrice);
    if (v.maxPrice) params.set('max_price', v.maxPrice);
    if (v.waterSupply?.length) params.set('water_supply', v.waterSupply.join(','));
    if (v.electricity?.length) params.set('electricity', v.electricity.join(','));
    if (v.connectivity?.length) params.set('connectivity', v.connectivity.join(','));
    if (v.bedrooms?.length) params.set('bedrooms', v.bedrooms.join(','));
    if (v.bathrooms?.length) params.set('bathrooms', v.bathrooms.join(','));
    if (v.conditionStatus?.length) params.set('condition_status', v.conditionStatus.join(','));
    if (v.conditionState?.length) params.set('condition_state', v.conditionState.join(','));
    if (v.floorLevel?.length) params.set('floor_level', v.floorLevel.join(','));
    if (v.features?.length) params.set('features', v.features.join(','));
    if (v.postedWithin && v.postedWithin !== 'any') params.set('posted_within', v.postedWithin);
    navigate(`/search?${params.toString()}`);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    submitWith();
  };

  if (compact) {
    return (
      <form onSubmit={handleSearch} data-testid="search-bar-compact" className="w-full">
        <div className="flex flex-col sm:flex-row gap-2 bg-white/95 backdrop-blur-sm rounded-2xl p-2 shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
          <Select value={stateKey || 'all'} onValueChange={onStateChange}>
            <SelectTrigger className="w-full sm:flex-1 sm:w-auto h-12 border-0 bg-slate-50 rounded-xl text-sm" data-testid="search-state-select">
              <SelectValue placeholder={language === 'es' ? 'Estado' : 'State'} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="search-state-opt-all">{language === 'es' ? 'Todos los Estados' : 'All States'}</SelectItem>
              {states.map(s => (
                <SelectItem key={s.key} value={s.key} data-testid={`search-state-opt-${s.key}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={cityMode === 'other' ? '__other__' : (city || 'all')} onValueChange={onCitySelect} disabled={!stateKey}>
            <SelectTrigger className="w-full sm:flex-1 sm:w-auto h-12 border-0 bg-slate-50 rounded-xl text-sm disabled:opacity-60" data-testid="search-city-select">
              <SelectValue placeholder={
                !stateKey
                  ? (language === 'es' ? 'Selecciona Estado' : 'Pick state first')
                  : (language === 'es' ? 'Ciudad' : 'City')
              } />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="search-city-opt-all">{language === 'es' ? 'Todas las Ciudades' : 'All Cities'}</SelectItem>
              {cities.map(c => (
                <SelectItem key={c.key} value={c.name} data-testid={`search-city-opt-${c.key}`}>{c.name}</SelectItem>
              ))}
              <SelectItem value="__other__" data-testid="search-city-opt-other">{language === 'es' ? 'Otra…' : 'Other…'}</SelectItem>
            </SelectContent>
          </Select>
          {cityMode === 'other' && (
            <Input
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder={language === 'es' ? 'Escribe ciudad' : 'Type city'}
              className="w-full sm:w-44 h-12 border-0 bg-slate-50 rounded-xl text-sm"
              data-testid="search-city-other-input"
            />
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full sm:flex-1 sm:w-auto h-12 border-0 bg-slate-50 rounded-xl text-sm justify-between font-normal" data-testid="search-type-select">
                {selectedTypes.length ? `${selectedTypes.length} ${language === 'es' ? 'tipos' : 'types'}` : t('types.all')}
                <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="start">
              {PROPERTY_TYPES.map(pt => (
                <button key={pt.value} type="button" onClick={() => toggleType(pt.value)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-slate-50 transition-colors text-left"
                  data-testid={`type-opt-${pt.value}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selectedTypes.includes(pt.value) ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-slate-300'}`}>
                    {selectedTypes.includes(pt.value) && <Check className="w-3 h-3 text-[#0B192C]" />}
                  </div>
                  {pt.label}
                </button>
              ))}
              {selectedTypes.length > 0 && (
                <button type="button" onClick={() => setSelectedTypes([])} className="w-full text-xs text-slate-400 hover:text-slate-600 mt-1 py-1">
                  {language === 'es' ? 'Limpiar' : 'Clear all'}
                </button>
              )}
            </PopoverContent>
          </Popover>
          <Select value={operationType || 'all'} onValueChange={(v) => setOperationType(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:flex-1 sm:w-auto h-12 border-0 bg-slate-50 rounded-xl text-sm" data-testid="search-operation-select">
              <SelectValue placeholder={t('operations.all')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('operations.all')}</SelectItem>
              <SelectItem value="sale">{t('operations.sale')}</SelectItem>
              <SelectItem value="rent">{t('operations.rent')}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" className="h-12 px-8 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold" data-testid="search-submit-btn">
            <Search className="w-4 h-4 mr-2" />{t('search.search')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSearch} data-testid="search-bar-full" className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">{language === 'es' ? 'Estado' : 'State'}</label>
          <Select value={stateKey || 'all'} onValueChange={onStateChange}>
            <SelectTrigger className="h-10 bg-slate-50 border-slate-200 rounded-lg" data-testid="filter-state">
              <SelectValue placeholder={language === 'es' ? 'Estado' : 'State'} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="filter-state-opt-all">{language === 'es' ? 'Todos los Estados' : 'All States'}</SelectItem>
              {states.map(s => (
                <SelectItem key={s.key} value={s.key} data-testid={`filter-state-opt-${s.key}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">{t('search.city')}</label>
          <Select value={cityMode === 'other' ? '__other__' : (city || 'all')} onValueChange={onCitySelect} disabled={!stateKey}>
            <SelectTrigger className="h-10 bg-slate-50 border-slate-200 rounded-lg disabled:opacity-60" data-testid="filter-city">
              <SelectValue placeholder={
                !stateKey
                  ? (language === 'es' ? 'Primero Estado' : 'Pick state first')
                  : (language === 'es' ? 'Ciudad' : 'City')
              } />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="filter-city-opt-all">{language === 'es' ? 'Todas las Ciudades' : 'All Cities'}</SelectItem>
              {cities.map(c => (
                <SelectItem key={c.key} value={c.name} data-testid={`filter-city-opt-${c.key}`}>{c.name}</SelectItem>
              ))}
              <SelectItem value="__other__" data-testid="filter-city-opt-other">{language === 'es' ? 'Otra…' : 'Other…'}</SelectItem>
            </SelectContent>
          </Select>
          {cityMode === 'other' && (
            <Input
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder={language === 'es' ? 'Escribe ciudad' : 'Type city'}
              className="mt-2 h-10 bg-slate-50 border-slate-200 rounded-lg"
              data-testid="filter-city-other-input"
            />
          )}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-500 mb-1 block">{t('search.propertyType')}</label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full h-10 bg-slate-50 border-slate-200 rounded-lg text-sm justify-between font-normal" data-testid="filter-type">
              {selectedTypes.length ? `${selectedTypes.length} ${language === 'es' ? 'tipos' : 'types'}` : t('types.all')}
              <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-2" align="start">
            {PROPERTY_TYPES.map(pt => (
              <button key={pt.value} type="button" onClick={() => toggleType(pt.value)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-slate-50 transition-colors text-left"
                data-testid={`filter-type-opt-${pt.value}`}>
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selectedTypes.includes(pt.value) ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-slate-300'}`}>
                  {selectedTypes.includes(pt.value) && <Check className="w-3 h-3 text-[#0B192C]" />}
                </div>
                {pt.label}
              </button>
            ))}
            {selectedTypes.length > 0 && (
              <button type="button" onClick={() => setSelectedTypes([])} className="w-full text-xs text-slate-400 hover:text-slate-600 mt-1 py-1">
                {language === 'es' ? 'Limpiar' : 'Clear all'}
              </button>
            )}
          </PopoverContent>
        </Popover>
        {selectedTypes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {selectedTypes.map(st => (
              <Badge key={st} variant="outline" className="text-[10px] gap-1 cursor-pointer hover:bg-red-50" onClick={() => toggleType(st)}>
                {PROPERTY_TYPES.find(p => p.value === st)?.label} ×
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={operationType || 'all'} onValueChange={(v) => setOperationType(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-10 bg-slate-50 border-slate-200 rounded-lg" data-testid="filter-operation">
            <SelectValue placeholder={t('operations.all')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('operations.all')}</SelectItem>
            <SelectItem value="sale">{t('operations.sale')}</SelectItem>
            <SelectItem value="rent">{t('operations.rent')}</SelectItem>
          </SelectContent>
        </Select>
        <Input type="number" placeholder={t('search.minPrice')} value={minPrice} onChange={e => setMinPrice(e.target.value)}
          className="h-10 bg-slate-50 border-slate-200 rounded-lg" data-testid="filter-min-price" />
        <Input type="number" placeholder={t('search.maxPrice')} value={maxPrice} onChange={e => setMaxPrice(e.target.value)}
          className="h-10 bg-slate-50 border-slate-200 rounded-lg" data-testid="filter-max-price" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="flex-1 min-w-[80px] h-10 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-lg font-medium" data-testid="filter-search-btn">
          <Search className="w-4 h-4 mr-1.5" />{t('search.search')}
        </Button>
        <Button type="button" variant="outline" className="h-10 rounded-lg gap-1.5 text-xs px-2.5" data-testid="filter-attributes-btn"
          onClick={() => setShowAttributes(!showAttributes)}>
          <Home className="w-4 h-4" />{language === 'es' ? 'Atributos' : 'Attributes'}
          {(bedrooms.length + bathrooms.length + conditionStatus.length + conditionState.length + floorLevel.length + features.length + (postedWithin !== 'any' ? 1 : 0)) > 0 && (
            <span className="ml-1 bg-[#D4AF37] text-[#0B192C] rounded-full px-1.5 text-[9px] font-bold">
              {bedrooms.length + bathrooms.length + conditionStatus.length + conditionState.length + floorLevel.length + features.length + (postedWithin !== 'any' ? 1 : 0)}
            </span>
          )}
        </Button>
        <Button type="button" variant="outline" className="h-10 rounded-lg gap-1.5 text-xs px-2.5" data-testid="filter-utilities-btn"
          onClick={() => setShowAdvanced(!showAdvanced)}>
          <SlidersHorizontal className="w-4 h-4" />{language === 'es' ? 'Servicios' : 'Utilities'}
        </Button>
        <Button type="button" variant="outline" className="h-10 rounded-lg text-xs px-2.5" data-testid="filter-reset-btn" onClick={resetAll}>
          {t('search.reset')}
        </Button>
      </div>

      {showAttributes && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-4 animate-fade-in" data-testid="attribute-filters">
          {/* Bedrooms / Bathrooms */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Building className="w-3.5 h-3.5 text-slate-400" />{language === 'es' ? 'Habitaciones' : 'Bedrooms'}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="filter-bedrooms">
                {BEDROOM_OPTIONS.map(o => {
                  const checked = bedrooms.includes(o.key);
                  return (
                    <button key={o.key} type="button" onClick={() => setBedrooms(checked ? bedrooms.filter(x => x !== o.key) : [...bedrooms, o.key])}
                      className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                      data-testid={`filter-bedrooms-${o.key}`}>
                      {language === 'es' ? o.es : o.en}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Building className="w-3.5 h-3.5 text-slate-400" />{language === 'es' ? 'Baños' : 'Bathrooms'}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="filter-bathrooms">
                {BATHROOM_OPTIONS.map(o => {
                  const checked = bathrooms.includes(o.key);
                  return (
                    <button key={o.key} type="button" onClick={() => setBathrooms(checked ? bathrooms.filter(x => x !== o.key) : [...bathrooms, o.key])}
                      className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                      data-testid={`filter-bathrooms-${o.key}`}>
                      {language === 'es' ? o.es : o.en}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Condition Status / Condition State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">{language === 'es' ? 'Situación de la vivienda' : 'Property Condition'}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="filter-condition-status">
                {CONDITION_STATUS_OPTIONS.map(o => {
                  const checked = conditionStatus.includes(o.key);
                  return (
                    <button key={o.key} type="button" onClick={() => setConditionStatus(checked ? conditionStatus.filter(x => x !== o.key) : [...conditionStatus, o.key])}
                      className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                      data-testid={`filter-condition-status-${o.key}`}>
                      {language === 'es' ? o.es : o.en}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">{language === 'es' ? 'Estado' : 'Condition'}</label>
              <div className="flex flex-wrap gap-1.5" data-testid="filter-condition-state">
                {CONDITION_STATE_OPTIONS.map(o => {
                  const checked = conditionState.includes(o.key);
                  return (
                    <button key={o.key} type="button" onClick={() => setConditionState(checked ? conditionState.filter(x => x !== o.key) : [...conditionState, o.key])}
                      className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                      data-testid={`filter-condition-state-${o.key}`}>
                      {language === 'es' ? o.es : o.en}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Floor Level */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">{language === 'es' ? 'Elevación' : 'Floor Level'}</label>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-floor-level">
              {FLOOR_LEVEL_OPTIONS.map(o => {
                const checked = floorLevel.includes(o.key);
                return (
                  <button key={o.key} type="button" onClick={() => setFloorLevel(checked ? floorLevel.filter(x => x !== o.key) : [...floorLevel, o.key])}
                    className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`filter-floor-level-${o.key}`}>
                    {language === 'es' ? o.es : o.en}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Features */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />{language === 'es' ? 'Características' : 'Features'}</label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="filter-features">
              {FEATURE_OPTIONS.map(o => {
                const checked = features.includes(o.key);
                return (
                  <button key={o.key} type="button" onClick={() => setFeatures(checked ? features.filter(x => x !== o.key) : [...features, o.key])}
                    className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border text-left ${checked ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`filter-feature-${o.key}`}>
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-slate-300 bg-white'}`}>
                      {checked && <Check className="w-2.5 h-2.5 text-[#0B192C]" />}
                    </div>
                    <span className="truncate">{language === 'es' ? o.es : o.en}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Publication date */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-400" />{language === 'es' ? 'Fecha de publicación' : 'Publication Date'}</label>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-posted-within">
              {PUBLICATION_WINDOW_OPTIONS.map(o => {
                const checked = postedWithin === o.key;
                return (
                  <button key={o.key} type="button" onClick={() => setPostedWithin(o.key)}
                    className={`text-xs px-2.5 py-1 rounded-md border ${checked ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0B192C] font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`filter-posted-within-${o.key}`}>
                    {language === 'es' ? o.es : o.en}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {showAdvanced && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-3 animate-fade-in" data-testid="utility-filters">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Droplets className="w-3.5 h-3.5 text-blue-500" />{language === 'es' ? 'Agua' : 'Water Supply'}</label>
            <UtilityCheckboxes category="water_supply" selected={waterSupply} onChange={setWaterSupply} compact />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-amber-500" />{language === 'es' ? 'Electricidad' : 'Electricity'}</label>
            <UtilityCheckboxes category="electricity" selected={electricity} onChange={setElectricity} compact />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Wifi className="w-3.5 h-3.5 text-green-500" />{language === 'es' ? 'Conectividad' : 'Connectivity'}</label>
            <UtilityCheckboxes category="connectivity" selected={connectivity} onChange={setConnectivity} compact />
          </div>
        </div>
      )}
    </form>
  );
}
